import React, { useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { Trash2, Loader2 } from 'lucide-react';
import { KnowledgeUpload } from './components/KnowledgeUpload/KnowledgeUpload';
import { getFileMeta, formatBytes } from '../../components/knowledge/KnowledgeUtils';
import { formatDate } from '../../../../shared/utils/date';

import { useKnowledgeData } from './hooks/useKnowledgeData';
import { KnowledgeHeader } from './components/KnowledgeHeader';
import { KnowledgeControls } from './components/KnowledgeControls';
import { KnowledgeCards } from './components/KnowledgeCards';
import { KnowledgeEmptyState } from './components/KnowledgeEmptyState';

type Screen = 'list' | 'upload';

export function KnowledgeBase() {
  const adminUserRaw = localStorage.getItem('admin_user');
  const adminUser = adminUserRaw ? JSON.parse(adminUserRaw) : null;
  
  if (adminUser?.role === 'member') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const [screen, setScreen] = useState<Screen>('list');
  const [searchParams] = useSearchParams();
  const urlOrgId = searchParams.get('orgId') || undefined;
  
  const {
    isSuperAdmin,
    search,
    setSearch,
    catFilter,
    setCatFilter,
    fileTypeFilter,
    setFileTypeFilter,
    sortFilter,
    setSortFilter,
    orgFilter,
    docs,
    filtered,
    handleDelete,
    showSkeleton,
    totalStorage,
    categoriesCount
  } = useKnowledgeData(urlOrgId);

  if (screen === 'upload') {
    return <KnowledgeUpload onCancel={() => setScreen('list')} onSuccess={() => setScreen('list')} targetOrgId={orgFilter} />;
  }

  return (
    <div style={{ width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', paddingBottom: '40px' }}>
      
      <KnowledgeHeader 
        totalDocuments={docs.length}
        categoriesCount={categoriesCount}
        totalStorage={totalStorage}
      />

      <KnowledgeControls 
        search={search}
        setSearch={setSearch}
        catFilter={catFilter}
        setCatFilter={setCatFilter}
        sortFilter={sortFilter}
        setSortFilter={setSortFilter}
        onUploadClick={() => setScreen('upload')}
      />

      <KnowledgeCards 
        selectedType={fileTypeFilter}
        onSelectType={setFileTypeFilter}
      />

      {/* Document List or Empty State */}
      {showSkeleton ? (
        <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--admin-card-border)', borderRadius: '0', overflow: 'hidden', backgroundColor: 'var(--admin-bg-container, #010204)' }}>
          {[1, 2, 3].map((idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px', borderBottom: idx !== 3 ? '1px solid var(--admin-card-border)' : 'none', flexWrap: 'wrap' }}>
              <div className="admin-skeleton" style={{ width: '38px', height: '38px', borderRadius: '0', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div className="admin-skeleton" style={{ width: 180, height: 14, borderRadius: 0 }} />
                <div className="admin-skeleton" style={{ width: 280, height: 12, borderRadius: 0 }} />
              </div>
              <div className="admin-skeleton" style={{ width: 80, height: 26, borderRadius: 0, flexShrink: 0 }} />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <KnowledgeEmptyState onUploadClick={() => setScreen('upload')} selectedType={fileTypeFilter} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--admin-card-border)', borderRadius: '0', overflow: 'hidden', backgroundColor: 'var(--admin-bg-container, #010204)' }}>
          {filtered.map((doc, idx) => {
            const { icon, color, label } = getFileMeta(doc.file_name);
            return (
              <div key={doc.id}
                style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px', borderBottom: idx !== filtered.length - 1 ? '1px solid var(--admin-card-border)' : 'none', flexWrap: 'wrap', transition: 'background 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>

                {/* File icon */}
                <div style={{ width: '38px', height: '38px', borderRadius: '0', backgroundColor: `${color}15`, border: `1px solid ${color}28`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
                  {icon}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span className="text-sm font-bold" style={{ color: 'var(--admin-card-text)' }}>{doc.title}</span>
                    <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '0', backgroundColor: `${color}12`, color, border: `1px solid ${color}25`, letterSpacing: '.03em' }}>{label}</span>
                    {doc.category && (
                      <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '0', backgroundColor: 'rgba(56,189,248,0.1)', color: 'var(--admin-sidebar-logo-text)', textTransform: 'uppercase' }}>{doc.category}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '4px', flexWrap: 'wrap' }}>
                    <span className="text-xs font-medium" style={{ color: 'var(--admin-card-subtext)' }}>{doc.file_name}</span>
                    <span className="text-xs font-medium" style={{ color: 'var(--admin-card-subtext)' }}>·</span>
                    <span className="text-xs font-medium" style={{ color: 'var(--admin-card-subtext)' }}>{formatBytes(doc.file_size)}</span>
                    {doc.created_at && <>
                      <span className="text-xs font-medium" style={{ color: 'var(--admin-card-subtext)' }}>·</span>
                      <span className="text-xs font-medium" style={{ color: 'var(--admin-card-subtext)' }}>{formatDate(doc.created_at)}</span>
                    </>}
                    {doc.uploader_name && <>
                      <span className="text-xs font-medium" style={{ color: 'var(--admin-card-subtext)' }}>·</span>
                      <span className="text-xs font-medium" style={{ color: 'var(--admin-card-subtext)' }}>by {doc.uploader_name}</span>
                    </>}
                  </div>
                </div>

                {/* Delete */}
                <button onClick={() => handleDelete(doc)}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 13px', borderRadius: '0', border: '1px solid rgba(239,68,68,0.2)', backgroundColor: 'rgba(239,68,68,0.07)', color: '#EF4444', fontSize: '11px', fontWeight: 800, cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s', textTransform: 'uppercase' }}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
